import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartPageConfig } from '@front-platform/content-builder';
import { cartPageResolveConfig } from '@front-platform/content-builder';
import dayjs from 'dayjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, interval, of, throwError } from 'rxjs';
import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { catchError, skipWhile, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { SELECT_ORDER_VALIDATION } from '@store/order';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { CartService } from '@core/services/cart/cart.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { OrdersService } from '@core/services/orders/orders.service';
import { BasketService } from '@core/services/basket/basket.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';

import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { CartFooterModalComponent } from '@shared/modals/cart-footer-modal/cart-footer-modal.component';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { bindCurrentPageShopFromCart } from './cart-page-current-shop.util';

export interface CartPageRuntimeConfig {
  'top'?: unknown[];
  'left'?: unknown[];
  'right'?: unknown[];
}

export type CartPageConfigInput = Partial<
  Omit<CartPageConfig, keyof CartPageRuntimeConfig> & CartPageRuntimeConfig
>;
export type CartPageResolvedConfig = Omit<CartPageConfig, keyof CartPageRuntimeConfig> &
  Partial<CartPageRuntimeConfig>;
export interface CartPageContract extends ConfigComponentContract<
  CartPageConfigInput,
  CartPageResolvedConfig
> {}

@Component({
  selector: 'app-cart-page',
  templateUrl: './cart-page.component.html',
  standalone: false,
})
export class CartPageComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, CartPageContract
{
  readonly config = input<CartPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<CartPageConfig, CartPageRuntimeConfig>(
    cartPageResolveConfig,
    () => ({ config: this.config() }),
  );
  get top(): unknown[] {
    return this.resolvedConfig()['top'] as unknown as unknown[];
  }
  get left(): unknown[] {
    return this.resolvedConfig()['left'] as unknown as unknown[];
  }
  get right(): unknown[] {
    return this.resolvedConfig()['right'] as unknown as unknown[];
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get backgroundImageCss(): string | null {
    const url = this.backgroundImage?.url;
    return url ? `url(${url})` : null;
  }

  orderTimeWarningMessage = '';

  constructor(
    private store: Store,
    private readonly router: Router,
    private cartSelector: CartStoreSelector,
    private shopSelector: ShopStoreSelector,
    private readonly shopDispatcher: ShopStoreDispatcher,
    private readonly cartService: CartService,
    private basketSelector: BasketStoreSelector,
    private readonly basketService: BasketService,
    private readonly modalsService: ModalsService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly ordersService: OrdersService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routerHelper: RouterHelperService,
    private readonly orderTypeService: OrderTypeService,
    private readonly translateService: TranslateService,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.handleCmiPaymentErrorOnRedirect();

    // Per-shop cart blocks (suggestion cards, …) evaluate `shop` from currentPage.
    this.cartService.loadShopsOfCart$().pipe(takeUntil(this._destroyerRef)).subscribe();
    bindCurrentPageShopFromCart(
      this.cartSelector.selectActive$,
      id => this.shopDispatcher.setCurrentPageShop(id),
      this._destroyerRef,
    );

    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'cart',
      this.activatedRoute.snapshot,
    );

    const selectedDistributionMode = this.cartSelector.active?.type;

    if (selectedDistributionMode) {
      let capabilities = this.orderTypeService.getCapabilities(selectedDistributionMode);

      if (capabilities.preorderingAllowed) {
        this.ordersService
          .loadShopSlots$()
          .pipe(
            take(1),
            switchMap(() => this.orderDispatcher.checkCartIsValid$()),
          )
          .subscribe();
      } else {
        this.orderDispatcher.checkCartIsValid();
      }
    }

    this.modalsService.open(CartFooterModalComponent.handle);

    this.store
      .select(SELECT_ORDER_VALIDATION)
      .pipe(
        takeUntil(this._destroyerRef),
        tap(orderValidate => {
          if (!orderValidate) {
            this.orderTimeWarningMessage = '';
            return;
          }

          this.getOrderTimeIndicateMessage(orderValidate);
        }),
      )
      .subscribe();

    this.subscribeToSharedOrderUpdates();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.shopDispatcher.setCurrentPageShop(null);
    this.modalsService.close(CartFooterModalComponent.handle);
  }

  private handleCmiPaymentErrorOnRedirect(): void {
    this.activatedRoute.queryParams
      .pipe(
        take(1),
        switchMap(queryParams => {
          if (queryParams.error) {
            return this.translateService
              .get(`payment.${queryParams.error}` || 'payment.unknown-error')
              .pipe(
                take(1),
                tap(translation => {
                  this.modalsService.open(AlertModalComponent.handle);
                  this.modalsService.setData(AlertModalComponent.handle, {
                    message: translation,
                  });
                }),
              );
          }
          return of();
        }),
      )
      .subscribe();
  }

  private subscribeToSharedOrderUpdates(): void {
    this.basketSelector.select
      .pipe(
        skipWhile(basket => !basket.id || !basket.share_code),
        take(1),
        switchMap(basket => this.basketService.getBasket$(basket.id, basket.share_code)),
        switchMap(basket => {
          return combineLatest([of(basket), this.orderDispatcher.checkCartIsValid$()]);
        }),
        switchMap(() => interval(15000)),
        takeUntil(this._destroyerRef),
        switchMap(() =>
          this.basketService.getBasket$(
            this.basketSelector.basket.id,
            this.basketSelector.basket.share_code,
          ),
        ),
        catchError(error => {
          if (error.status === 403) {
            this.translateService
              .get('group-order-modal.order-cancelled')
              .pipe(
                take(1),
                tap(translation => {
                  alert(translation);
                  this.ordersService.clearOrders();
                  this.basketService.clearBasket();
                  this.cartService.clearCart();
                  this.router.navigate([this.routerHelper.translateRoute('/')]);
                }),
              )
              .subscribe();
          }
          return throwError(() => error);
        }),
        switchMap(() => this.orderDispatcher.checkCartIsValid$()),
      )
      .subscribe();
  }

  private getOrderTimeIndicateMessage(order: {
    wanted_at?: Date | string | null;
    ready_at?: Date | string | null;
  }): void {
    this.orderTimeWarningMessage = '';

    const readyAt = this.normalizeReadyAt(order.ready_at ?? order.wanted_at);
    if (!readyAt) {
      return;
    }

    const wantedAtTime = this.normalizeReadyAt(order.wanted_at);

    if (wantedAtTime && !this.isFirstSlot(wantedAtTime)) {
      return;
    }

    const orderReadyAtTime = dayjs(readyAt);
    const isTodayOrder = orderReadyAtTime.isToday();
    const isOrderTimeIsAfterNext2Hours = orderReadyAtTime.isAfter(
      dayjs().add(1, 'hours').add(59, 'minutes'),
    );
    if (!isOrderTimeIsAfterNext2Hours) {
      return;
    }

    if (isTodayOrder) {
      const hoursDiff = orderReadyAtTime.diff(dayjs(), 'hours');
      if (hoursDiff >= 2) {
        this.orderTimeWarningMessage = `${this.translateService.instant('order.parameters-overview.order-is-today')} ${hoursDiff} ${this.translateService.instant('order.parameters-overview.hours')}`;
      }
      return;
    }

    const today = dayjs().startOf('day');
    const orderReadyAtTimeStartDay = dayjs(orderReadyAtTime).startOf('day');
    const daysDiff = orderReadyAtTimeStartDay.diff(today, 'days');

    if (daysDiff === 1) {
      this.orderTimeWarningMessage = `${this.translateService.instant('order.parameters-overview.order-is-tomorrow')}`;
    } else {
      this.orderTimeWarningMessage = `${this.translateService.instant('order.parameters-overview.order-is-later')} ${daysDiff} ${this.translateService.instant('order.parameters-overview.days')}`;
    }
  }

  private normalizeReadyAt(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private isFirstSlot(wantedAt: Date, marginInMinutes = 15): boolean {
    const slots = this.shopSelector.slots;

    const specificDate = dayjs(wantedAt);
    if (!slots || !slots.length) {
      return false;
    }

    return slots.some(timestamp => {
      const dateFromTimestamp = dayjs(timestamp * 1000);
      const differenceInMinutes = Math.abs(specificDate.diff(dateFromTimestamp, 'minute'));

      return differenceInMinutes <= marginInMinutes;
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartPageComponent, { 'keyAliases': {} });
